import { useState, useEffect, useMemo } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import Image from 'common/image'
import { useTranslation } from 'react-i18next'
import { Navigation, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import './my-custom-bullet.css'
import { adobeInteractionEvent } from 'utils/adobewa'

const WhatIsNewContainer = styled.div`
    width: 100%;
    background: #efeade;
`

const TitleContainer = styled.div`
    margin-top: 56px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-bottom: 32px;
`

const TitleText = styled.div`
    color: #121b21;
    font-size: 32px;
    font-style: normal;
    font-weight: 700;
    line-height: 40px;
`
const SwiperSlideframe = styled.div`
    height: 464px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
`
const CustomSwiperSlide = styled(SwiperSlide)`
    width: 304px;
    border-radius: 50px;
    background-color: white;
`

const SwiperLowerPart = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 30px 30px;
`

const SwiperSlideTitle = styled.div`
    color: #222;
    font-size: 14px;
    font-weight: bold;
    line-height: 20px; /* 175% */
    margin-bottom: 12px;
`

const SwiperSlideDescription = styled.div`
    color: #d2a461;
    font-size: 18px;
    line-height: 25px;
    min-height: 150px;
`
const SwiperSlideButton = styled.div`
    margin-top: 12px;
    width: 212px;
    border: 1px solid #d2a461;
    padding: 5px 30px;
    border-radius: 26px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
`

const SwiperSlideButtonText = styled.div`
    color: #d2a461;
    font-size: 16px;
    font-weight: bold;
`

export default function WhatIsNewSwiper() {
    const { t, i18n } = useTranslation('', { keyPrefix: 'homepage.whatisnew' })
    const lang = i18n.language
    const configs = useSelector((state: any) => state.config.configs)
    const [bannerAppeared, setBannerAppeared] = useState<number[]>([])
    const [swiperKey, setSwiperKey] = useState(0)
    const banners = useMemo(
        () => configs?.homepage?.carouselBanners || [],
        [configs]
    )

    const sortedBanners = [...banners].sort(function (a: any, b: any) {
        return a.id - b.id || a.titleEn.localeCompare(b.titleEn)
    })

    useEffect(() => {
        // Check if the Swiper instance exists and then call update()
        setSwiperKey((prevKey) => prevKey + 1)
    }, [banners])

    const redirectTo = (carouselBanner: any) => {
        const targetTitle =
            lang === 'zh' ? carouselBanner?.titleZh : carouselBanner?.titleEn
        const targetUrl =
            lang === 'zh'
                ? carouselBanner.destinationZh
                : carouselBanner.destinationEn

        adobeInteractionEvent({
            lang: lang,
            eventID: 'prioritysite_clickBanner_Home_WhatsNew',
            contentTitle: targetTitle,
            contentUrl: targetUrl,
        })

        window.open(targetUrl, '_blank')
    }

    const fireActiveBannerWAEvent = (index: number) => {
        const targetTitle =
            lang === 'zh' ? banners[index]?.titleZh : banners[index]?.titleEn
        const targetUrl =
            lang === 'zh'
                ? banners[index]?.destinationZh
                : banners[index]?.destinationEn

        if (!bannerAppeared.includes(index)) {
            adobeInteractionEvent({
                lang: lang,
                eventID: 'prioritysite_BannerImpression_Home_WhatsNew',
                contentTitle: targetTitle,
                contentUrl: targetUrl,
            })

            const newArray: number[] = bannerAppeared
            newArray.push(index)
            setBannerAppeared(newArray)
        }
    }

    return (
        <WhatIsNewContainer>
            <TitleContainer>
                <TitleText>{t(`WHATNEW`)}</TitleText>
            </TitleContainer>
            <Swiper
                key={swiperKey}
                loop={true}
                centeredSlides={true}
                slidesPerView={'auto'}
                initialSlide={0}
                spaceBetween={8}
                modules={[Navigation, Pagination]}
                pagination={{
                    clickable: true,
                }}
                autoplay={{
                    delay: 5000,
                    disableOnInteraction: false,
                }}
                speed={1000} // Adjust speed for desired effect
                onTransitionEnd={(swiper: any) =>
                    fireActiveBannerWAEvent(swiper?.realIndex)
                }
                style={{ paddingBottom: '60px' }}
            >
                {sortedBanners?.map((carouselBanner: any, index: number) => {
                    return (
                        <CustomSwiperSlide key={index}>
                            <SwiperSlideframe>
                                <Image
                                    src={
                                        lang === 'zh'
                                            ? carouselBanner.imageZh
                                            : carouselBanner.imageEn
                                    }
                                    width={304}
                                    height={158}
                                    borderradius={30}
                                    alt="Carousel Banner"
                                />
                                <SwiperLowerPart>
                                    <SwiperSlideTitle>
                                        {lang === 'zh'
                                            ? carouselBanner.titleZh
                                            : carouselBanner.titleEn}
                                    </SwiperSlideTitle>
                                    <SwiperSlideDescription
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                lang === 'zh'
                                                    ? carouselBanner.descriptionZh
                                                    : carouselBanner.descriptionEn,
                                        }}
                                    ></SwiperSlideDescription>
                                    {(lang === 'en' &&
                                        carouselBanner.buttonTextEn &&
                                        carouselBanner.destinationEn) ||
                                    (lang === 'zh' &&
                                        carouselBanner.buttonTextZh &&
                                        carouselBanner.destinationZh) ? (
                                        <SwiperSlideButton
                                            onClick={() =>
                                                redirectTo(carouselBanner)
                                            }
                                        >
                                            <SwiperSlideButtonText>
                                                {lang === 'zh'
                                                    ? carouselBanner.buttonTextZh
                                                    : carouselBanner.buttonTextEn}
                                            </SwiperSlideButtonText>
                                        </SwiperSlideButton>
                                    ) : null}
                                </SwiperLowerPart>
                            </SwiperSlideframe>
                        </CustomSwiperSlide>
                    )
                })}
            </Swiper>
        </WhatIsNewContainer>
    )
}
